import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/database'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  }
})

// Enhanced database helpers
export const db = {
  companies: {
    create: async (data: Database['public']['Tables']['companies']['Insert']) => {
      try {
        console.log('🏢 Creating company with data:', data)
        
        const { data: company, error } = await supabase
          .from('companies')
          .insert(data)
          .select()
          .single()
        
        if (error) {
          console.error('❌ Company creation error:', error)
          throw error
        }
        
        console.log('✅ Company created successfully:', company)
        return { data: company, error: null }
      } catch (error) {
        console.error('❌ Company create error:', error)
        return { data: null, error }
      }
    },
    
    getById: async (id: string) => {
      try {
        const result = await supabase.from('companies').select('*').eq('id', id).single()
        return result
      } catch (error) {
        console.error('❌ Company getById error:', error)
        throw error
      }
    },
    
    update: async (id: string, data: Database['public']['Tables']['companies']['Update']) => {
      try {
        const result = await supabase.from('companies').update(data).eq('id', id).select().single()
        return result
      } catch (error) {
        console.error('❌ Company update error:', error)
        throw error
      }
    }
  },

  users: {
    create: async (data: Database['public']['Tables']['users']['Insert']) => {
      try {
        console.log('👤 Creating user with data:', data)
        
        let retries = 3
        let lastError = null
        
        while (retries > 0) {
          try {
            const { data: userRecord, error } = await supabase
              .from('users')
              .insert(data)
              .select()
              .single()
            
            if (error) {
              console.error('❌ User creation error:', error)
              lastError = error
              retries--
              if (retries > 0) {
                console.log(`🔄 Retrying user creation... ${retries} attempts left`)
                await new Promise(resolve => setTimeout(resolve, 1000))
                continue
              }
              throw error
            }
            
            console.log('✅ User created successfully:', userRecord)
            return { data: userRecord, error: null }
          } catch (error) {
            lastError = error
            retries--
            if (retries > 0) {
              console.log(`🔄 Retrying user creation... ${retries} attempts left`)
              await new Promise(resolve => setTimeout(resolve, 1000))
            }
          }
        }
        
        throw lastError
      } catch (error) {
        console.error('❌ User create error:', error)
        return { data: null, error }
      }
    },
    
    findById: async (id: string) => {
      try {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', id)
          .maybeSingle()
        
        if (userError) throw userError
        
        return { data: userData, error: null }
      } catch (error) {
        console.error('❌ User findById error:', error)
        return { data: null, error }
      }
    },
    
    getById: async (id: string) => {
      try {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select(`
            *,
            companies (
              id,
              name,
              industry
            )
          `)
          .eq('id', id)
          .single()
        
        if (userError) throw userError
        
        return { data: userData, error: null }
      } catch (error) {
        console.error('❌ User getById error:', error)
        return { data: null, error }
      }
    },
    
    getByEmail: async (email: string) => {
      try {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select(`
            *,
            companies (
              id,
              name,
              industry
            )
          `)
          .eq('email', email)
          .single()
        
        if (userError) throw userError
        
        return { data: userData, error: null }
      } catch (error) {
        console.error('❌ User getByEmail error:', error)
        return { data: null, error }
      }
    },
    
    update: async (id: string, data: Database['public']['Tables']['users']['Update']) => {
      try {
        const result = await supabase.from('users').update(data).eq('id', id).select().single()
        return result
      } catch (error) {
        console.error('❌ User update error:', error)
        throw error
      }
    }
  },

  employees: {
    create: async (data: Database['public']['Tables']['employees']['Insert']) => {
      try {
        const result = await supabase.from('employees').insert(data).select().single()
        return result
      } catch (error) {
        console.error('❌ Employee create error:', error)
        throw error
      }
    },
    
    getByCompany: async (companyId: string) => {
      try {
        const result = await supabase
          .from('employees')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
        return result
      } catch (error) {
        console.error('❌ Employee getByCompany error:', error)
        throw error
      }
    },
    
    getById: async (id: string) => {
      try {
        const result = await supabase.from('employees').select('*').eq('id', id).single()
        return result
      } catch (error) {
        console.error('❌ Employee getById error:', error)
        throw error
      }
    },

    getByEmail: async (email: string) => {
      try {
        const result = await supabase
          .from('employees')
          .select('*')
          .eq('email', email)
          .single()
        return result
      } catch (error) {
        console.error('❌ Employee getByEmail error:', error)
        throw error
      }
    },
    
    getByCompanyAndEmail: async (companyId: string, email: string) => {
      try {
        const result = await supabase
          .from('employees')
          .select('*')
          .eq('company_id', companyId)
          .eq('email', email)
        return result
      } catch (error) {
        console.error('❌ Employee getByCompanyAndEmail error:', error)
        throw error
      }
    },
    
    update: async (id: string, data: Database['public']['Tables']['employees']['Update']) => {
      try {
        const result = await supabase.from('employees').update(data).eq('id', id).select().single()
        return result
      } catch (error) {
        console.error('❌ Employee update error:', error)
        throw error
      }
    },
    
    delete: async (id: string) => {
      try {
        const result = await supabase.from('employees').delete().eq('id', id)
        return result
      } catch (error) {
        console.error('❌ Employee delete error:', error)
        throw error
      }
    }
  },

  assessments: {
    create: async (data: any) => {
      try {
        const result = await supabase.from('assessments').insert(data).select().single()
        return result
      } catch (error) {
        console.error('❌ Assessment create error:', error)
        throw error
      }
    },

    getByEmployee: async (employeeId: string) => {
      try {
        const result = await supabase
          .from('assessments')
          .select('*')
          .eq('employee_id', employeeId)
          .order('created_at', { ascending: false })
        return result
      } catch (error) {
        console.error('❌ Assessment getByEmployee error:', error)
        throw error
      }
    },

    getByCompany: async (companyId: string) => {
      try {
        const result = await supabase
          .from('assessments')
          .select(`
            *,
            employees (
              first_name,
              last_name,
              department,
              email
            )
          `)
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
        return result
      } catch (error) {
        console.error('❌ Assessment getByCompany error:', error)
        throw error
      }
    },
    
    getById: async (id: string) => {
      try {
        const result = await supabase
          .from('assessments')
          .select('*')
          .eq('id', id)
          .single()
        return result
      } catch (error) {
        console.error('❌ Assessment getById error:', error)
        throw error
      }
    }
  },

  assessmentTokens: {
    create: async (data: any) => {
      try {
        const result = await supabase.from('assessment_tokens').insert(data).select().single()
        return result
      } catch (error) {
        console.error('❌ Assessment token create error:', error)
        throw error
      }
    },

    getByToken: async (token: string) => {
      try {
        const result = await supabase
          .from('assessment_tokens')
          .select(`
            *,
            employees (
              id,
              first_name,
              last_name,
              email,
              department,
              company_id
            )
          `)
          .eq('token', token)
          .eq('is_used', false)
          .gte('expires_at', new Date().toISOString())
          .single()
        return result
      } catch (error) {
        console.error('❌ Assessment token getByToken error:', error)
        throw error
      }
    },

    markAsUsed: async (token: string) => {
      try {
        const result = await supabase
          .from('assessment_tokens')
          .update({ is_used: true, used_at: new Date().toISOString() })
          .eq('token', token)
        return result
      } catch (error) {
        console.error('❌ Assessment token markAsUsed error:', error)
        throw error
      }
    }
  }
}

// Test database connection
export const testConnection = async () => {
  try {
    const { data, error } = await supabase.from('companies').select('count').limit(1)
    if (error) throw error
    console.log('✅ Database connection successful')
    return true
  } catch (error) {
    console.error('❌ Database connection failed:', error)
    return false
  }
}